/**
 * Mutate the active Prompt Lab session's user prompt.
 *
 * Sibling to setSystemPrompt. Same approval semantics: local state,
 * but the model must not overwrite the user's text without confirmation.
 */

import type { NipeTool } from './types';

export interface SetUserPromptContext {
  /** Returns the active sessionId. Null when Prompt Lab is booting. */
  getActivePromptSessionId?: () => string | null;
  /** Dispatches `updateSession({ id, changes: { userPrompt } })`. */
  setPromptSessionUserPrompt?: (
    sessionId: string,
    text: string,
  ) => { ok: true } | { ok: false; reason: string };
}

const MAX_PROMPT_BYTES = 200_000;

export const setUserPromptTool: NipeTool<SetUserPromptContext> = {
  schema: {
    type: 'function',
    function: {
      name: 'setUserPrompt',
      description:
        "Replace the user prompt on the user's active Prompt Lab session. " +
        'Use this only after the user has agreed to your proposed rewrite, ' +
        'or after they explicitly asked you to draft the user prompt for ' +
        'them. Read the current userPrompt via readPromptSession before ' +
        'overwriting so you can preserve anything they wanted to keep.',
      parameters: {
        type: 'object',
        properties: {
          text: {
            type: 'string',
            description:
              'The new user-prompt text. Replaces the existing value ' +
              'verbatim. Max 200,000 chars.',
          },
        },
        required: ['text'],
      },
    },
  },
  mutatesRemote: false,
  requiresApproval: true,
  handler: async (args, ctx) => {
    if (!ctx.getActivePromptSessionId || !ctx.setPromptSessionUserPrompt) {
      return {
        ok: false,
        reason: 'Prompt Lab is not mounted in this session.',
      };
    }
    const text = typeof args.text === 'string' ? args.text : '';
    if (text.length > MAX_PROMPT_BYTES) {
      return {
        ok: false,
        reason: `User prompt too large (${text.length} > ${MAX_PROMPT_BYTES} bytes).`,
      };
    }
    const sessionId = ctx.getActivePromptSessionId();
    if (sessionId === null) {
      return {
        ok: false,
        reason: 'No active prompt session yet (Prompt Lab is still booting).',
      };
    }
    return ctx.setPromptSessionUserPrompt(sessionId, text);
  },
};
